import moment from "moment-mini";
import {Engine} from "platform/engine/Engine";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import Utils from "platform/util/Utils";
import {ServiceType} from "enum/ServiceType";
import {
    FetchAccountEquityLogsPayload,
    SetAccountEquityLogs,
    SetAccountLogs,
    SetAccountLogsPending, SetPortfolioTables,
    SetPortfolioTabPayload
} from "core/redux/portfolio/PortfolioReduxActions";
import {PortfolioType} from "enum/PortfolioType";
import {GetAccountLogRequest} from "protocol/account/GetAccountLogRequest";
import {AccountState} from "core/state/AccountState";
import DateTimeFormat from "core/format/DateTimeFormat";
import {FilterDateState, FilterDateType} from "core/redux/filter/FilterReduxState";
import {FilterUtil} from "core/util/FilterUtil";
import {XhrUtil} from "core/util/XhrUtil";
import {AccountLog} from "protocol/account/AccountLog";
import {PortfolioDateFilterPayload} from "core/redux/filter/FilterReduxActions";
import {DealingLogType} from "enum/DealingLogType";
import {TSMap} from "typescript-map";
import {StorageKey} from "enum/StorageKey";
import {PortfolioTableState} from "core/redux/portfolio/PortfolioReduxState";

export default class PortfolioEngine extends Engine {

    private static _instance: PortfolioEngine;

    public static instance(): PortfolioEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        const tables: TSMap<PortfolioType, PortfolioTableState> = await this.deserializeLS();
        Platform.dispatch(SetPortfolioTables({tables}));
    }

    private deserializeLS = async (): Promise<TSMap<PortfolioType, PortfolioTableState>> => {
        const tables: TSMap<PortfolioType, PortfolioTableState> = await PortfolioType.deserialize(StorageKey.PortfolioSort);
        return Promise.resolve(tables);
    }

    public onSetPortfolioTab = (payload: SetPortfolioTabPayload): void => {
        if (payload.portfolioType === PortfolioType.ActivityLog) {
            const filter: FilterDateState = Platform.reduxState<StoreState>().filter.portfolioDateFilter.get(PortfolioType.ActivityLog);
            this.fetchActivityLog(filter.dateType, filter.from, filter.to);
        }
    }

    public onSetPortfolioDateFilter = (payload: PortfolioDateFilterPayload): void => {
        if (payload.portfolioType === PortfolioType.ActivityLog) {
            this.fetchActivityLog(payload.dateType, payload.from, payload.to);
        }
    }

    private fetchActivityLog = (dateType: FilterDateType, from: moment.Moment, to: moment.Moment): void => {
        const range: { from: Date, to: Date } = FilterUtil.getDateRange(dateType, from, to);
        Platform.dispatch(SetAccountLogsPending({pending: true}));
        this.fetchDealingLogs(range.from, range.to, []).then((logs: AccountLog[]) => {
                this._logger.debug("Received account logs: " + logs.length);
                Platform.dispatch(SetAccountLogsPending({pending: false}));
                logs.sort((l1: AccountLog, l2: AccountLog) => Utils.compareNumber(l2.DateTime, l1.DateTime));
                Platform.dispatch(SetAccountLogs({logs}));
            })
            .catch(() => {
                this._logger.error("Failed fetch account logs");
                Platform.dispatch(SetAccountLogsPending({pending: false}));
            });
    }

    private fetchDealingLogs = async (from: Date, to: Date, filterActionTypes: DealingLogType[]): Promise<AccountLog[]> => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const request: GetAccountLogRequest = {
            AccountId: accountState.accountId,
            from: DateTimeFormat.formatServerDate(from),
            to: DateTimeFormat.formatServerDate(to),
            filterActionTypes
        };
        return await XhrUtil.sendAuthenticated(request, "TradeServer/SearchLogProvider.svc/json/GetAccountDealingLog");
    }

    public doFetchAccountEquityLogs = ({from, to}: FetchAccountEquityLogsPayload): void => {
        this.fetchDealingLogs(from, to, [DealingLogType.WithdrawableEquity]).then((logs: AccountLog[]) => {
            const equityLogs: AccountLog[] = logs.sort((l1: AccountLog, l2: AccountLog) => Utils.compareNumber(l1.DateTime, l2.DateTime));
            if (Utils.isArrayNotEmpty(equityLogs)) {
                const {WithdrawableEquity} = Platform.reduxState<StoreState>().account;
                if (WithdrawableEquity) {
                    equityLogs.push({
                        WithdrawableEquity,
                        DateTime: new Date().getTime()
                    });
                }
            }
            Platform.dispatch(SetAccountEquityLogs({
                logs: equityLogs
            }));
        });
    }
}
